'use client';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface Category {
  id: string;
  name: string;
  slug: string;
  count?: number;
}

interface Props {
  categories: Category[] | undefined;
  value: string;
  onChange: (slug: string) => void;
  /** Сколько специалистов в категории в выбранном городе (ТЗ 5) */
  facets?: Record<string, number>;
  /** Всего специалистов в городе — счётчик чипа «Все» */
  facetsTotal?: number;
}

const ALL: Category = { id: '__all__', name: 'Все', slug: '' };

const chipClass = (active: boolean) =>
  `flex-shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
    active ? 'bg-brand text-white border-brand' : 'bg-brand-muted text-brand border-transparent hover:bg-brand/10'
  }`;

export default function CategoryChips({ categories, value, onChange, facets, facetsTotal }: Props) {
  // Порядок — по убыванию числа специалистов в городе; пустые категории
  // не показываем, иначе клик по чипу вёл на пустой экран (ТЗ 5).
  // Пока фасеты не пришли, показываем всё в исходном порядке — чтобы ряд
  // чипов не мигал и не перестраивался на каждой загрузке.
  const hasFacets = !!facets;
  const items = useMemo(() => {
    const all = categories ?? [];
    if (!hasFacets) return [ALL, ...all];
    const withCounts = all
      .map(c => ({ ...c, count: facets?.[c.slug] ?? 0 }))
      .filter(c => c.count > 0 || c.slug === value)
      .sort((a, b) => b.count - a.count);
    return [{ ...ALL, count: facetsTotal ?? 0 }, ...withCounts];
  }, [categories, facets, facetsTotal, hasFacets, value]);

  const chipLabel = (cat: Category & { count?: number }) =>
    typeof cat.count === 'number' ? `${cat.name} · ${cat.count}` : cat.name;
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const recalc = () => {
      const container = containerRef.current;
      const measure = measureRef.current;
      if (!container || !measure) return;
      const containerWidth = container.offsetWidth;
      const chipEls = Array.from(measure.children) as HTMLElement[];
      const moreWidth = 92; // резерв под кнопку "Ещё ▾"
      const gap = 8;
      let used = 0;
      let count = 0;
      for (let i = 0; i < chipEls.length; i++) {
        const w = chipEls[i].offsetWidth + (i > 0 ? gap : 0);
        const isLast = i === chipEls.length - 1;
        const reserve = isLast ? 0 : moreWidth;
        if (used + w + reserve > containerWidth) break;
        used += w;
        count++;
      }
      setVisibleCount(Math.max(1, count));
    };
    recalc();
    const ro = new ResizeObserver(recalc);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('resize', recalc);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recalc);
    };
  }, [items.length]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  const activeInOverflow = overflow.some(c => c.slug === value);

  return (
    <div className="relative w-full min-w-0">
      {/* Скрытый измеритель для расчёта, сколько чипов влезает на десктопе */}
      <div ref={measureRef} className="absolute -z-10 opacity-0 pointer-events-none flex gap-2" aria-hidden="true">
        {items.map(cat => (
          <span key={cat.id} className={chipClass(cat.slug === value)}>{chipLabel(cat)}</span>
        ))}
      </div>

      {/* Мобильный: горизонтальный скролл со scroll-snap */}
      <div
        className="flex gap-2 overflow-x-auto sm:hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        style={{ scrollSnapType: 'x proximity' }}
      >
        {items.map(cat => (
          <button
            key={cat.id}
            type="button"
            aria-pressed={cat.slug === value}
            onClick={() => onChange(cat.slug)}
            className={chipClass(cat.slug === value)}
            style={{ scrollSnapAlign: 'start' }}
          >
            {chipLabel(cat)}
          </button>
        ))}
      </div>

      {/* Десктоп: влезающие чипы + "Ещё ▾" */}
      <div ref={containerRef} className="hidden sm:flex items-center gap-2 min-w-0">
        {visible.map(cat => (
          <button
            key={cat.id}
            type="button"
            aria-pressed={cat.slug === value}
            onClick={() => onChange(cat.slug)}
            className={chipClass(cat.slug === value)}
          >
            {chipLabel(cat)}
          </button>
        ))}
        {overflow.length > 0 && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(v => !v)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              className={`${chipClass(activeInOverflow)} inline-flex items-center gap-1`}
            >
              Ещё {overflow.length}
              <ChevronDown size={14} className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
            </button>
            {menuOpen && (
              <div className="absolute left-0 top-full mt-1 w-48 max-h-72 overflow-y-auto bg-bg-surface rounded-xl border border-border shadow-lg z-40 py-1">
                {overflow.map(cat => (
                  <button
                    key={cat.id}
                    type="button"
                    aria-pressed={cat.slug === value}
                    onClick={() => { onChange(cat.slug); setMenuOpen(false); }}
                    className={`w-full text-left px-3.5 py-2.5 text-sm transition-colors focus:outline-none focus-visible:bg-bg-subtle hover:bg-bg-subtle ${cat.slug === value ? 'text-brand font-semibold' : 'text-text-body'}`}
                  >
                    {chipLabel(cat)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
